import { formatMoney, isGlobalEFormatEnv } from "@/utils/CommonUtils";
import { useGeDisplayMarked } from "@/contexts/cart-ge-eligibility";
import { localeDependentProps } from "@/libs/locale/locale-ready";

// Global-E 가격 마커 컴포넌트. docs/global-e-storefront.md §3.3 / §1.1.
//
// - **마커(`data-ge-price`)**: 비-KR(commerce country) 판매가에만 부착 → Global-E 가
//   해당 가격을 현지통화로 변환. KR/미설정이면 마커 미부착(§1.1 KR 차단의 유일 가드).
//   마커 자체는 inert — Global-E `globalEEnabled` flag off(prod 현재)면 무시되어 영향 없음.
// - **포맷**: 마커 가격은 Global-E 기대형 `{symbol} {price}`(예: "$ 200"). spaced 는
//   비-prod **이면서 마커가 붙는(비-KR) 가격에만** 적용(`isGlobalEFormatEnv && marked`).
//   prod 은 현행 포맷 유지, KR/미설정은 dev 에서도 현행 포맷 → prod 가시 변경 0. §4.8.
//
// 사용: 가격 노출 지점에서 `{formatMoney({amount, currency, countryCode})}` 를
//      `<GePrice amount={..} currency={..} countryCode={..} />` 로 교체.
// 제외: order history(주문완료/tracking)·인보이스·이메일 — 마커 부착 안 함(과거 기록).

interface GePriceProps {
  amount: number;
  currency?: string;
  countryCode?: string | null;
  className?: string;
  // formatMoney 의 hideCurrency 패스스루 (예: 줄긋기 원가에서 통화코드 숨김).
  hideCurrency?: boolean;
  /**
   * Global-E **basket integration** 속성 키. 주면 마커가 붙는(비-KR) 경우에 한해
   * `data-ge-basket-{basketAttr}` 를 함께 부착한다 → Global-E 가 장바구니 항목가/합계를 읽는다.
   * 값 없는 presence 속성(요소 텍스트=가격). 장바구니(미니카트)에서만 사용.
   * 키 집합(2026-06-15 Global-E 스펙): productsubtotalprice / producttotalprice /
   * subtotals / productdiscountsprice / totals. (cartitemid 는 가격 요소가 아니라 row 에 직접 부착.)
   * 할인 키는 `discounts` → `productdiscountsprice` 로 정정(2026-06-18 Global-E 회신):
   * GE 가 `data-ge-basket-discounts` 를 통화 변환 시 0 으로 처리(original=59→converted=0)했고,
   * GE 가 인식하는 키는 `productdiscountsprice` 라 확인됨. docs §3.3.1.
   */
  basketAttr?: string;
}

export function GePrice({
  amount,
  currency,
  countryCode,
  className,
  hideCurrency,
  basketAttr,
}: GePriceProps) {
  // GE 표시 마커 부착 여부 = 비-KR + carrier 적격 + Dynamic(!isFixed) 단일 판정(cart-item 등과 공유).
  // 비-GE(무거움) 또는 Fixed(US/EU/GB) 카트면 억제 → 우리 값/USD 표시. Dynamic 만 GE 변환/합산.
  // 카트 밖(PDP/카드)은 provider 없어 기본값 → country 기준(현행).
  const marked = useGeDisplayMarked(countryCode);

  // spaced(`{symbol} {price}`)는 마커가 붙는(=Global-E 가 변환할) 가격에만 의미가 있다.
  // KR/미설정은 마커가 없으므로 현행 포맷 유지 → dev 에서도 prod 와 동일하게 보임.
  const text = formatMoney({
    amount,
    currency,
    countryCode,
    hideCurrency,
    spaced: isGlobalEFormatEnv && marked,
  });

  // basket 속성도 KR 가드(marked) 안에서만 부착 — KR 카트엔 Global-E 속성 미부착(§1.1).
  const basket =
    basketAttr && marked
      ? ({ [`data-ge-basket-${basketAttr}`]: "" } as Record<
          `data-${string}`,
          string
        >)
      : undefined;

  return marked ? (
    // `localeDependentProps`: 국가 확정 전엔 숨긴다 — 확정 전 가격은 URL 로케일 폴백 국가(`/en` → US) 값이다
    //
    // ★`key={text}` — 가격이 바뀌면 span 을 **새로 만든다**(텍스트만 갈아 끼우지 않는다). GE 엔진은 변환한 요소에
    //   인라인 `visibility: visible` 을 남기는데, 같은 요소의 텍스트만 원래 가격(`$ 799`)으로 바뀌면 이미 보이는
    //   상태라 GE 가 다시 변환할 때까지 원래 가격이 노출된다(옵션 변경 깜빡임 — GE Kyuri 2026-10-19 진단·처방).
    //   새 요소는 인라인 스타일이 없어 GE 가 변환을 마칠 때까지 숨겨진다. 같은 가격이면 key 가 같아 그대로 둔다.
    //   비마커(`data-gem-ignore`) 쪽은 GE 가 손대지 않으므로 필요 없다.
    <span
      key={text}
      data-ge-price
      {...basket}
      {...localeDependentProps}
      className={className}
    >
      {text}
    </span>
  ) : (
    // 비-GE(비적격/Fixed/KR)·provider 밖 → GE 통화 변환을 **차단**한다(`data-gem-ignore`, GE 공식
    // storefront-attributes). GE 는 마커(`data-ge-price`) 유무와 무관하게 페이지의 가격 텍스트를 자체
    // 검출해 현지통화로 바꾸므로(§4.11 실측), 변환을 원치 않는 가격엔 이 속성을 붙여야 우리 값(USD/KRW/
    // JPY)이 유지된다. marked 일 때만 GE 변환 허용. docs/global-e-storefront.md §4.11.
    <span data-gem-ignore {...localeDependentProps} className={className}>
      {text}
    </span>
  );
}
